import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { NumericFormat } from "react-number-format";
import { useQuery } from "@tanstack/react-query";
import { FormikState } from "formik";
import dayjs from "../../../../utils/dayjsConfig";

// HOOKS
import useScreenSize from "../../../../hooks/useScreenSize";
import { useModalBodyPadding } from "../../../../hooks/useModalBodyPadding";
import { useGtmEvent } from "../../../../hooks/useGtmEvent";

// REDUX
import { useAppSelector } from "../../../../store/hooks";
import {
  updateOperationMEPID,
  updateOperationMEPModal,
} from "../../../../store/slices/operationMEPSlice";

// CONSTANTS
import { MOBILE_OPERATE_WIDTH } from "../../constants";

// TYPES
import { OperateFormValues } from "../../types";

// CONNECTORS
import {
  createMepOperation,
  getAffidavitStatus,
  isResident,
  notifySignDocument,
} from "../../../../connectors/operationApi";

//UTILS
import {
  defaultNumericFormatProps,
  genericErrorMessage,
  marketIsClosedErrorMessageNextDay,
  marketIsClosedErrorMessageTomorrow,
} from "../../../../utils";
import {
  cleanInputString,
  getAffidavitPath,
  isArgentinaMarketOpen,
  parseToFloat,
} from "../../utils";
import { addMessage } from "../../../../utils/addMessage";
import { openExternal } from "../../../NavBar/utils/utils";
import { triggerBalanceRefetch } from "../../../../store/slices/userAccountsSlice";

//COMPONENTS
import { ModalBody, ModalFooter, ModalHeader } from "@nextui-org/react";
import Button from "../../../Button/Button";
import Spinner from "../../../Spinner/Spinner";
import { BackHeader } from "../BackHeader/BackHeader";

//STYLES
import styles from "./ReviewModal.module.scss";

//ASSETS
import CaseIcon from "../../../../assets/images/icons/operateModal/dollar-icon.svg?react";
import Alert from "../../../Alert/Alert";

interface ReviewModalProps {
  resetForm: (
    nextState?: Partial<FormikState<OperateFormValues>> | undefined,
  ) => void;
  setFieldValue: (field: string, value: unknown) => void;
  acceptedTermsAndConditions: boolean;
  mepQuote: { BUY: number; SELL: number };
  handleClose: () => void;
  refetchPositions: () => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  resetForm,
  setFieldValue,
  acceptedTermsAndConditions,
  mepQuote,
  handleClose,
  refetchPositions,
}) => {
  const {
    header,
    cardsContainer,
    amountCard,
    amountRow,
    label,
    value,
    currency,
    symbolCurrency,
    footer,
    acceptTermsAndConditions,
    checkbox,
    spinnerContainer,
    contentSpinner,
    button,
    amountCardContainer,
    headerMobile,
    bodyMobile,
    container,
    alertInfo,
  } = styles;

  const { isMobile } = useScreenSize(MOBILE_OPERATE_WIDTH);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [hideAffidavitCheckbox, setHideAffidavitCheckbox] = useState(true);
  const dispatch = useDispatch();
  const { pushEvent } = useGtmEvent();

  // -------- Operation Data -------- //

  const { data: operationMEPData } = useAppSelector(
    (state) => state.operationMEPData,
  );
  const { account, side, operationValueArs, operationValueMep } =
    operationMEPData || {};
  const isBuySide = side === "BUY";

  const { isHolidayToday, nextBusinessDay } = useAppSelector(
    (state) => state.marketStatus,
  );
  const isMarketOpen = isArgentinaMarketOpen() && !isHolidayToday;

  // -------- Account Data -------- //

  const { accounts } = useAppSelector((state) => state.userAccounts);
  const selectedAccount = accounts.find((acc) => acc.number === account);
  const accountId = selectedAccount?.number || "";
  const isJuridical = selectedAccount?.isJuridical;

  // -------- Affidavit -------- //

  const { data: affidavitStatus, isLoading: isLoadingAffidavitStatus } =
    useQuery({
      queryFn: () => getAffidavitStatus({ accountId: accountId }),
      queryKey: ["affidavit", accountId],
      enabled: !!accountId,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 0,
      gcTime: 0,
    });

  useEffect(() => {
    if (
      !!affidavitStatus &&
      ((isBuySide && affidavitStatus?.sell) ||
        (!isBuySide && affidavitStatus?.buy))
    ) {
      setFieldValue("acceptedTermsAndConditions", true);
      setHideAffidavitCheckbox(true);
    } else {
      setHideAffidavitCheckbox(false);
    }
  }, [affidavitStatus]);

  // -------- Is Resident -------- //

  const { data: isResidentData, isLoading: isLoadingResidentData } = useQuery({
    queryFn: () => isResident({ accountId: accountId }),
    queryKey: ["isResident", accountId],
    enabled: !!accountId && !isJuridical,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
  });

  // -------- Actions -------- //

  const handleGoBack = () => {
    dispatch(updateOperationMEPModal("DRAFT"));
  };

  const refetchBalance = () => {
    setTimeout(() => {
      dispatch(triggerBalanceRefetch());
    }, 4000);
  };

  const handleConfirm = async () => {
    setSubmitLoading(true);
    try {
      if (!isMarketOpen) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowFormatted = tomorrow.toISOString().split("T")[0];
        if (nextBusinessDay === tomorrowFormatted)
          throw "Market is closed. Try again tomorrow";
        else throw "Market is closed. Try again on the nex business day";
      }

      if (!hideAffidavitCheckbox) {
        const invertedSide = side === "BUY" ? "SELL" : "BUY";
        await notifySignDocument({
          accountId,
          documentType: "AFFIDAVIT",
          description: invertedSide,
        });
      }

      const data = {
        amount: isBuySide
          ? (Number(operationValueArs) ?? 0)
          : (Number(operationValueMep) ?? 0),
        estimated_price: isBuySide ? mepQuote.BUY : mepQuote.SELL,
      };

      const res = await createMepOperation({
        side: side?.toLowerCase() || "",
        data,
        accountId,
      });

      dispatch(updateOperationMEPID(res?.id));
      setFieldValue("isResident", isResidentData ?? true);
      dispatch(updateOperationMEPModal("SUMMARY"));
      resetForm();
      pushEvent("completed_operation", { symbol_type: "DOLLAR_MEP", side });
      window?.Iterate?.("event", "operatoria");
      // Refetch positions after successful operation
      refetchPositions();
      refetchBalance();
    } catch (e) {
      // Convert error to string to safely check for error messages
      const errorMessage =
        typeof e === "string"
          ? e
          : (e as { message?: string; response?: { data?: { description?: string } } })?.message ||
            (e as { response?: { data?: { description?: string } } })?.response?.data?.description ||
            String(e ?? "");

      const marketIsClosedErrorMessage = errorMessage.includes(
        "Market is closed",
      )
        ? errorMessage.includes("tomorrow")
          ? marketIsClosedErrorMessageTomorrow
          : marketIsClosedErrorMessageNextDay
        : "";

      addMessage({
        messageId: "operateMEPModal",
        message: marketIsClosedErrorMessage || genericErrorMessage,
        type: "error",
      });
      console.error(e);
    } finally {
      setSubmitLoading(false);
    }
  };

  // -------- Data -------- //

  const data = [
    {
      label: "CUENTA",
      value: `${selectedAccount?.name}`,
      text: `${account}`,
    },
    ...(isJuridical || !(isResidentData ?? true)
      ? [
          {
            label: "LIQUIDACIÓN",
            value: nextBusinessDay
              ? dayjs(nextBusinessDay).format("DD/MM/YYYY")
              : "-",
          },
        ]
      : []),
  ];

  const ARS_LIMIT = 1000000;
  const operationValueArsFloat = parseToFloat(
    cleanInputString(operationValueArs ?? ""),
  );
  const exceedsLimit = operationValueArsFloat > ARS_LIMIT;
  const isEstimatedTitle = !exceedsLimit || (isJuridical && isBuySide);
  const estimatedTitle = isEstimatedTitle
    ? "MONTO ESTIMADO A ACREDITAR"
    : "MONTO A ACREDITAR";

  const cardsData = [
    {
      title: "MONTO A DEBITAR",
      operationValue: isBuySide ? operationValueArs : operationValueMep,
      currencyCode: isBuySide ? "ARS" : "MEP",
    },
    {
      title: estimatedTitle,
      operationValue: isBuySide ? operationValueMep : operationValueArs,
      currencyCode: isBuySide ? "MEP" : "ARS",
    },
  ];

  const getHeaderText = () => {
    return `${side === "BUY" ? "Compra" : "Venta"} de Dólar MEP`;
  };

  // -------- Checkbox change -------- //

  const onCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFieldValue("acceptedTermsAndConditions", event.target.checked);
  };

  // -------- Aux components -------- //

  const getCards = ({
    title,
    operationValue,
    currencyCode,
  }: {
    title: string;
    operationValue: string | undefined;
    currencyCode: string;
  }) => {
    return (
      <div className={amountCard}>
        <div className={amountRow}>
          <span className={label}>{title}</span>
          <span className={value}>
            <span className={symbolCurrency}>$</span>
            <NumericFormat
              {...defaultNumericFormatProps}
              prefix=""
              value={operationValue}
            />
            <span className={currency}>{currencyCode}</span>
          </span>
        </div>
      </div>
    );
  };

  const Footer = useMemo(() => {
    return (
      <>
        {isMobile && (
          <div className={amountCardContainer}>
            {cardsData.map((obj) => getCards(obj))}
          </div>
        )}

        <Button
          text="Volver"
          variant="secondary"
          className={button}
          onPress={handleGoBack}
          isDisabled={submitLoading}
          isLoading={submitLoading}
        />
        <Button
          text="Confirmar"
          className={button}
          onPress={handleConfirm}
          isDisabled={submitLoading || !acceptedTermsAndConditions}
          isLoading={submitLoading}
        />
      </>
    );
  }, [handleConfirm, submitLoading, handleGoBack]);

  // -------- Body padding -------- //
  // Calculate dynamic body padding (after submitLoading and Footer)
  const { bodyPadding, headerRef, bodyRef, footerRef, containerRef } =
    useModalBodyPadding({
      isMobile,
      dependencies: [
        submitLoading,
        Footer,
        hideAffidavitCheckbox,
        acceptedTermsAndConditions,
        affidavitStatus,
        isResidentData,
      ],
    });

  if (isLoadingAffidavitStatus || isLoadingResidentData) {
    return (
      <div className={contentSpinner}>
        <div className={spinnerContainer}>
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <>
      <ModalHeader
        className={`${headerMobile} ${!isMobile && "p-0 pb-[1.5rem]"}`}
      >
        <div ref={headerRef}>
          {isMobile && <BackHeader onClose={handleClose} />}
          <div className={header}>
            <CaseIcon />
            <p>{getHeaderText()}</p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className={bodyMobile}>
        <div ref={bodyRef}>
          <div
            ref={containerRef}
            className={container}
            style={
              isMobile && bodyPadding > 0
                ? { paddingBottom: `${bodyPadding}px` }
                : undefined
            }
          >
            {/* Section data */}
            <div className={cardsContainer}>
              {data.map((item, i) => (
                <div key={i}>
                  <p>{item.label}</p>
                  <p>{item.value}</p>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>

            {!isMobile && cardsData.map((obj) => getCards(obj))}

            {isJuridical && isBuySide && (
              <Alert variant="INFO" className={alertInfo}>
                Para operar con dólar MEP es necesario transferirlos a un banco
                e ingresarlos nuevamente.{" "}
                <span
                  onClick={() =>
                    openExternal(
                      "https://www.bcra.gob.ar/Pdfs/comytexord/A7340.pdf",
                      "_blank",
                    )
                  }
                >
                  Comunicación “A” 7340 del BCRA.
                </span>
              </Alert>
            )}

            {!hideAffidavitCheckbox && (
              <div className={acceptTermsAndConditions}>
                <input
                  type="checkbox"
                  className={checkbox}
                  checked={acceptedTermsAndConditions}
                  onChange={onCheckboxChange}
                />
                <span>
                  Leí y acepto la{" "}
                  <a
                    href={getAffidavitPath({
                      isJuridical,
                      isBuySide,
                      isResident: isResidentData ?? true,
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    declaración jurada
                  </a>
                </span>
              </div>
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter
        className={` ${footer} ${isMobile ? "flex-col !p-0 p-0" : "pl-0 pb-0"}`}
      >
        <div ref={footerRef}>{Footer}</div>
      </ModalFooter>
    </>
  );
};

export default ReviewModal;
